import type { ReactNode } from 'react';

import { cn } from '@/shared/ui/cn';

export type SkeletonProps = {
  className?: string;
  /** Cercle : un avatar en attente. */
  circle?: boolean;
};

/**
 * Forme grise qui respire à la place d'un contenu qui arrive. Un écran qui
 * charge montre sa silhouette, jamais un tourniquet au milieu du vide.
 */
export function Skeleton({ className, circle = false }: SkeletonProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'block animate-shimmer bg-fill',
        circle ? 'rounded-pill' : 'rounded-sm',
        className,
      )}
    />
  );
}

/** Quelques lignes de texte en attente. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span aria-hidden className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5', index === lines - 1 ? 'w-3/5' : 'w-full')}
        />
      ))}
    </span>
  );
}

/** Une ligne de liste en attente : rond, deux lignes. */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('flex items-center gap-3 px-4 py-3', className)}>
      <Skeleton circle className="size-10" />
      <span className="flex flex-1 flex-col gap-2">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-3/5" />
      </span>
    </span>
  );
}

/** Écran qui charge : l'annonce pour les lecteurs d'écran, la silhouette pour les yeux. */
export function LoadingRegion({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={className}>
      {children}
    </div>
  );
}
